import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  FileText, Upload, CheckCircle, Clock, XCircle, Trash2, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageLayout from '../components/PageLayout';
import InfoBox from '../components/InfoBox';
import NotApplicable from '../components/NotApplicable';
import { useSiteSettings } from '../SiteSettingsContext';

const LICENSE_TYPES = [
  { value: 'medical_license', label: 'Medical License' },
  { value: 'dea_certificate', label: 'DEA Certificate' },
  { value: 'board_certification', label: 'Board Certification' },
  { value: 'anesthesia_certification', label: 'Anesthesia Certification' },
  { value: 'other', label: 'Other Credential' },
];

const StatusBadge = ({ status }) => {
  if (status === 'verified') return (
    <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
      <CheckCircle className="w-3 h-3" /> Verified
    </Badge>
  );
  if (status === 'rejected') return (
    <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
      <XCircle className="w-3 h-3" /> Rejected
    </Badge>
  );
  return (
    <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 gap-1">
      <Clock className="w-3 h-3" /> Pending Review
    </Badge>
  );
};

const LicenseUploadPage = () => {
  const { user, isAdmin } = useAuth();
  const { infoBoxes, licenseRequiredRoles } = useSiteSettings();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [licenseType, setLicenseType] = useState('medical_license');
  const [licenseExpiration, setLicenseExpiration] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [myTrips, setMyTrips] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchLicenses();
    api.get('/my-trips').then(res => setMyTrips(res.data || [])).catch(() => {});
    api.get('/profile').then(res => setProfile(res.data)).catch(() => {});
  }, []);

  const fetchLicenses = async () => {
    try {
      const res = await api.get('/licenses');
      setLicenses(res.data);
    } catch (error) {
      toast.error('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const params = new URLSearchParams({ license_type: licenseType });
      if (licenseExpiration) params.set('expiration_date', licenseExpiration);
      await api.post(`/licenses/upload?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('License uploaded successfully');
      setLicenseExpiration('');
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (licenseId) => {
    if (!window.confirm('Remove this license?')) return;
    try {
      await api.delete(`/licenses/${licenseId}`);
      toast.success('License removed');
      fetchLicenses();
    } catch (error) {
      toast.error('Failed to remove license');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const hasTrip = myTrips.length > 0;
  const requiresLicense = profile?.work_roles?.some(r =>
    licenseRequiredRoles.some(req => r.toLowerCase() === req.toLowerCase())
  );

  if (!loading && (!hasTrip || !requiresLicense)) {
    return (
      <PageLayout title="Credentials & Licenses" maxWidth="max-w-3xl">
        <NotApplicable
          title={!hasTrip ? "Not Required Yet" : "Not Required for Your Role"}
          message={!hasTrip
            ? "License uploads will be required once you are assigned to a trip."
            : "Based on your current work role, a license upload is not required. If this changes, this page will become active."}
          linkTo="/dashboard"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Credentials & Licenses" headerSub="Upload medical licenses and certifications for verification" maxWidth="max-w-3xl">
        {/* Upload Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" />
              Upload New Credential
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Credential Type</label>
              <Select value={licenseType} onValueChange={setLicenseType}>
                <SelectTrigger className="w-full" data-testid="license-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LICENSE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Expiration Date <span className="text-red-500">*</span></label>
              <Input
                type="date"
                value={licenseExpiration}
                onChange={(e) => setLicenseExpiration(e.target.value)}
                data-testid="license-expiration"
              />
            </div>

            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-rojo bg-rojo/5' : 'border-slate-200 hover:border-rojo/50'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="license-drop-zone"
            >
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700 mb-1">
                {uploading ? 'Uploading...' : 'Drop file here or click to browse'}
              </p>
              <p className="text-xs text-slate-400">PDF, JPG, PNG • Max 10MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files[0])}
              data-testid="license-file-input"
            />

            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full rounded-xl"
              data-testid="upload-license-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Select File to Upload'}
            </Button>
          </CardContent>
        </Card>

        {/* Uploaded Licenses */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Uploaded Credentials ({licenses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading...</div>
            ) : licenses.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No credentials uploaded yet</p>
                <p className="text-slate-400 text-xs mt-1">Upload your first credential above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {licenses.map((lic) => (
                  <div
                    key={lic.license_id}
                    className="flex items-start justify-between p-4 bg-slate-50 rounded-xl"
                    data-testid={`license-item-${lic.license_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{lic.file_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {LICENSE_TYPES.find(t => t.value === lic.license_type)?.label || lic.license_type}
                          {' · '}
                          {new Date(lic.uploaded_at).toLocaleDateString()}
                        </p>
                        {lic.expiration_date && (() => {
                          const exp = new Date(lic.expiration_date);
                          const now = new Date();
                          const sixMonths = new Date();
                          sixMonths.setMonth(sixMonths.getMonth() + 6);
                          const expired = exp < now;
                          const expiringSoon = !expired && exp < sixMonths;
                          return (
                            <p className={`text-xs mt-0.5 ${expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                              {expired ? '⚠ Expired' : expiringSoon ? '⚠ Expiring soon' : 'Expires'}: {exp.toLocaleDateString()}
                            </p>
                          );
                        })()}
                        {lic.notes && (
                          <p className="text-xs text-slate-500 mt-1 italic">Note: {lic.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <StatusBadge status={lic.status} />
                      <a
                        href={`${BACKEND_URL}/api/licenses/${lic.license_id}/file`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`view-license-${lic.license_id}`}
                      >
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                        onClick={() => handleDelete(lic.license_id)}
                        data-testid={`delete-license-${lic.license_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info box */}
        <InfoBox text={infoBoxes.license} className="mt-4">
          <p className="font-medium mb-1">Credential verification</p>
          <p>Uploaded credentials will be reviewed by trip administrators before your participation is confirmed. Please ensure your documents are current and legible.</p>
        </InfoBox>
    </PageLayout>
  );
};

export default LicenseUploadPage;
